/**
 * Lightweight HTML sanitizer for CMS content.
 * Strips <script> tags, on* event handlers, and javascript: URLs.
 * For admin-controlled CMS content this provides sufficient protection.
 *
 * Also normalises links: editors sometimes paste absolute URLs pointing at the
 * raw Firebase hosting domains (investsa-property[.--channel].web.app instead
 * of propinvest.co.za). Those leak the Firebase host to visitors and search
 * engines, and preview-channel URLs die when the channel expires — so they are
 * rewritten to relative paths (which also keeps preview channels self-contained).
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  return html
    // Remove <script> tags and their contents
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove on* event handlers (onclick, onerror, onload, etc.)
    .replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    // Remove javascript: URLs
    .replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"')
    .replace(/src\s*=\s*["']javascript:[^"']*["']/gi, 'src=""')
    // Rewrite Firebase-hosting URLs (incl. preview channels) to relative paths
    .replace(/https?:\/\/investsa-property(?:-dev)?(?:--[a-z0-9-]+)?\.web\.app(\/[^"'\s<>]*)?/gi, (_m, p) => p || '/')
    // Same-site absolute links → relative (keeps SPA/preview behaviour consistent)
    .replace(/https?:\/\/(?:www\.)?propinvest\.co\.za(\/[^"'\s<>]*)?/gi, (_m, p) => p || '/')
    // Mixed-content guard: http:// srcs in CMS HTML break the padlock — every
    // host we embed media from serves https, so upgrade defensively.
    .replace(/(src\s*=\s*["'])http:\/\//gi, '$1https://')
    .replace(/url\(\s*(["']?)http:\/\//gi, 'url($1https://');
}
